import { Loader2 } from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "react-toastify";
// import { generateReferralPDF } from "../../utils/generateReferralPDF";

const initialFormData = {
  referringParty: {
    name: "",
    agencyCompany: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    telephone: "",
    email: "",
  },
  receivingParty: {
    name: "",
    agencyCompany: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    telephone: "",
    email: "",
  },
  client: {
    name: "",
    businessAddress: "",
    city: "",
    state: "",
    zipCode: "",
    telephone: "",
    email: "",
    website: "",
  },
  referralFee: "",
  referralType: "percentage",
  startDate: "",
  endDate: "",
  referralIncludes: [],
  referralIncludesOther: "",
  additionalTerms: "",
};

const partyFields = {
  client: ["name", "agencyCompany", "businessAddress", "city", "state", "zipCode", "telephone", "email", "website"],
  referringParty: ["name", "agencyCompany", "businessAddress", "city", "state", "zipCode", "telephone", "email"],
  receivingParty: ["name", "agencyCompany", "businessAddress", "city", "state", "zipCode", "telephone", "email"],
};

const partyLabels = {
  name: "Name",
  agencyCompany: "Agency/Company",
  businessAddress: "Business Address",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  telephone: "Telephone",
  email: "Email",
  website: "Website",
};

const referralOptions = ["Buy", "Sell", "Lease", "Other"];

const Referrals = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Using dynamic refs
  const refs = useRef({});

  const handleChange = (e, partyKey) => {
    const { name, value, type, checked } = e.target;

    // Party-specific fields
    if (partyKey) {
      setFormData((prev) => ({
        ...prev,
        [partyKey]: { ...prev[partyKey], [name]: value },
      }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    } else if (type === "checkbox" && name === "referralIncludes") {
      // Only one selection at a time
      const newValue = checked ? value : null;
      setFormData((prev) => ({
        ...prev,
        referralIncludes: newValue,
        referralIncludesOther: value === "other" ? prev.referralIncludesOther : "",
      }));
      if (checked) setErrors((prev) => ({ ...prev, referralIncludes: undefined }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleQuickDate = (months) => {
    const today = new Date();
    const startDate = formData.startDate || today.toISOString().split("T")[0];
    const end = new Date(startDate);
    end.setMonth(end.getMonth() + months);

    setFormData((prev) => ({
      ...prev,
      startDate: prev.startDate || startDate,
      endDate: end.toISOString().split("T")[0],
    }));
    setErrors((prev) => ({ ...prev, startDate: undefined, endDate: undefined }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.referralFee) newErrors.referralFee = "Referral Fee required";
    if (!formData.startDate) newErrors.startDate = "Start date required";
    if (!formData.endDate) newErrors.endDate = "End date required";

    if (formData.referralIncludes.length === 0) newErrors.referralIncludes = "Select at least one";
    if (formData.referralIncludes.includes("other") && !formData.referralIncludesOther) newErrors.referralIncludesOther = "Specify other referral";

    // Validate parties dynamically
    Object.keys(partyFields).forEach((partyKey) => {
      partyFields[partyKey].forEach((field) => {
        if (!formData[partyKey][field] && field !== "website") {
          newErrors[field] = `${partyLabels[field]} required`;
        }
      });
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Focus first error
      const firstErrorField = Object.keys(newErrors)[0];
      refs.current[firstErrorField]?.focus();
      return false;
    }
    return true;
  };

  const quickFillReceivingParty = () => {
    setFormData((prev) => ({
      ...prev,
      receivingParty: { ...prev.referringParty },
    }));

    const newErrors = { ...errors };
    partyFields.receivingParty.forEach((field) => {
      delete newErrors[field];
    });
    setErrors(newErrors);

    toast.success("Receiving Party filled from Referring Party!");
  };

  // const generatePDF = () => {
  //   if (!validate()) {
  //     toast.error("Please fix validation errors before generating PDF");
  //     return;
  //   }

  //   try {
  //     const doc = generateReferralPDF(formData);
  //     doc.save(`Real-Estate-Referral-Agreement-${formData.client.name || "client"}.pdf`);
  //     toast.success("PDF generated successfully!");
  //   } catch (error) {
  //     console.error("Error generating PDF:", error);
  //     toast.error("Error generating PDF");
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Replace with real API call
      await new Promise((res) => setTimeout(res, 2000));
      console.log("Form submitted:", formData);

      //   const doc = generateReferralPDF(formData);
      //   doc.save(`Real-Estate-Referral-Agreement-${formData.client.name || "client"}.pdf`);

      toast.success("Referral submitted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Internal Server Error.");
    } finally {
      setLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  const renderPartySection = (title, partyKey, quickFill = false) => (
    <div className="border border-slate-400 rounded-xl mt-6">
      <div className="flex flex-col sm:flex-row justify-between items-center border-b border-slate-400 !py-2">
        <h2 className="text-left font-medium text-lg !px-6 ">{title}</h2>
        {quickFill && partyKey === "receivingParty" && (
          <button type="button" onClick={quickFillReceivingParty} className="sm:!mr-4 !px-3 !py-1 text-sm border border-[#008080] bg-[#008080] hover:bg-[#007070] text-white rounded font-medium cursor-pointer">
            Quick Fill from Referring Party
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 !py-6 !px-6">
        {partyFields[partyKey].map((field) => (
          <label key={field} className={`flex flex-col min-w-[200px] cursor-text !space-y-1`}>
            <span className="w-full text-left tracking-md text-slate-600">{partyLabels[field]} :</span>
            <input ref={(el) => (refs.current[field] = el)} type={field === "email" ? "email" : "text"} name={field} value={formData[partyKey][field]} onChange={(e) => handleChange(e, partyKey)} className={`outline-none border rounded-sm shadow w-full !px-2 !py-1 ${errors[field] ? "border-red-500" : "border-slate-400"}`} />
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="!my-6 !mx-auto max-w-6xl !px-4 !py-6 tracking-md">
      <h1 className="text-2xl font-semibold">Welcome to Real Estate Referrals!</h1>
      <p>Generate a referral agreement here and refer business to any real estate agent across the globe.</p>

      <form onSubmit={handleSubmit} className="!space-y-6 !mt-6">
        {renderPartySection("Referring Party", "referringParty")}
        {renderPartySection("Receiving Party", "receivingParty", true)}
        {renderPartySection("The Client", "client")}

        {/* Terms Section */}
        <div className="border border-slate-400 rounded-xl mt-6">
          <h2 className="text-left font-medium text-lg !px-6 !py-2 border-b border-slate-400">Terms</h2>
          <div className="flex flex-col gap-6 !py-6 !px-6">
            {/* Referral Fee */}
            <div className="flex flex-wrap gap-2 items-end">
              <label className="flex flex-col sm:flex-row item-center w-1/3 min-w-[200px] !space-x-1">
                <span className="text-slate-600 text-left text-nowrap block !mt-1 !mr-3">Referral Fee:</span>
                <input ref={(el) => (refs.current.referralFee = el)} type="number" name="referralFee" value={formData.referralFee} onChange={handleChange} placeholder={`Enter ${formData.referralType === "percentage" ? "percentage" : "amount"}`} className={`outline-none border rounded-sm shadow w-full !px-2 !py-1 ${errors.referralFee ? "border-red-500" : "border-slate-400"}`} />
              </label>
              <select name="referralType" value={formData.referralType} onChange={handleChange} className="border border-slate-400 rounded-sm !px-1 !py-1 shadow outline-none !w-12">
                <option value="percentage">%</option>
                <option value="flat">$</option>
              </select>
            </div>

            {/* Referral Dates */}
            <div className="flex flex-col sm:flex-row item-center">
              <div className="flex item-center !my-auto">
                <p className="text-slate-600 text-left text-nowrap block !mt-1 !mr-3 h-min">Referral dates: </p>
              </div>
              <div className="!space-y-1 w-min">
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                  <input ref={(el) => (refs.current.startDate = el)} type="date" name="startDate" min={todayStr} value={formData.startDate} onChange={handleChange} className={`outline-none border w-full sm:w-auto rounded-sm shadow !px-2 !py-1 ${errors.startDate ? "border-red-500" : "border-slate-400"}`} />
                  <span className="hidden sm:block">-</span>
                  <input ref={(el) => (refs.current.endDate = el)} type="date" name="endDate" min={todayStr} value={formData.endDate} onChange={handleChange} className={`outline-none border w-full sm:w-auto rounded-sm shadow !px-2 !py-1 ${errors.endDate ? "border-red-500" : "border-slate-400"}`} />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 !mt-1">
                  {[3, 6, 12].map((months) => {
                    const start = formData.startDate ? new Date(formData.startDate) : new Date();
                    const expectedEnd = new Date(start);
                    expectedEnd.setMonth(expectedEnd.getMonth() + months);
                    const isActive = formData.startDate && formData.endDate && new Date(formData.endDate).toISOString().split("T")[0] === expectedEnd.toISOString().split("T")[0];

                    return (
                      <button key={months} type="button" className={`!px-3 !py-1 border rounded w-full sm:w-1/3 shadow cursor-pointer ${isActive ? "bg-[#008080] text-white border-[#007070]" : "border-slate-400 bg-white text-black"}`} onClick={() => handleQuickDate(months)}>
                        {months === 12 ? "1 year" : `${months} months`}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Referral Includes */}
            <div className="flex flex-col gap-2">
              <span className="text-slate-600 font-medium text-left">Referral includes entering into contract to:</span>
              <div className="flex flex-col flex-wrap gap-2">
                {referralOptions.map((option) => (
                  <label key={option} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="referralIncludes" value={option.toLowerCase()} checked={formData.referralIncludes.includes(option.toLowerCase())} onChange={handleChange} className="accent-blue-600 cursor-pointer" />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {formData.referralIncludes.includes("other") && <input ref={(el) => (refs.current.referralIncludesOther = el)} type="text" name="referralIncludesOther" placeholder="Specify other referral" value={formData.referralIncludesOther} onChange={handleChange} className={`outline-none border rounded-sm shadow !px-2 !py-1 !mt-1 !ml-10 ${errors.referralIncludesOther ? "border-red-500" : "border-slate-400"}`} />}
              {errors.referralIncludes && <p className="text-red-500 text-sm text-left">{errors.referralIncludes}</p>}
            </div>

            {/* Additional Terms */}
            <div className="flex flex-col !space-y-1">
              <span className="text-slate-600 font-medium text-left">Additional Terms:</span>
              <textarea name="additionalTerms" rows="4" value={formData.additionalTerms} onChange={handleChange} placeholder="Enter any additional terms here..." className="outline-none border border-slate-400 rounded-sm shadow !px-2 !py-1 w-full" />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-4 mt-6">
          <button type="reset" disabled={loading} onClick={() => setFormData(initialFormData)} className={`!px-4 !py-2 shadow border-slate-300 bg-slate-200 hover:bg-slate-300 rounded font-medium text-black w-36 ${loading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
            Clear
          </button>
          {/* <button type="submit" disabled={loading} className={`!px-4 !py-2 shadow border-[#008080] bg-[#008080] hover:border-[#007070] hover:bg-[#007070] rounded font-medium text-white w-36 flex justify-center items-center ${loading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
            {loading ? <Loader2 size={24} className="animate-spin" /> : "Submit"}
          </button> */}
          {/* <button type="button" onClick={generatePDF} disabled={loading} className={`!px-4 !py-2 shadow border-[#0066cc] bg-[#0066cc] hover:border-[#0055aa] hover:bg-[#0055aa] rounded font-medium text-white w-36 flex justify-center items-center ${loading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
            Generate PDF
          </button> */}
          <button type="submit" disabled={loading} className={`!px-4 !py-2 shadow border-[#008080] bg-[#008080] hover:border-[#007070] hover:bg-[#007070] rounded font-medium text-white w-36 flex justify-center items-center ${loading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}>
            {loading ? <Loader2 size={24} className="animate-spin" /> : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Referrals;
